import { BlockOutlined, UserOutlined } from '@ant-design/icons'
import { StepEnum } from '@crm/api'
import {
  ActivityCol,
  CampaignsCol,
  ContractsCol,
  DocumentsCol,
  LeadsCol,
  OffersCol,
  OpportunitiesCol,
  PaymentAccountsCol,
  TasksCol
} from '@crm/components/InfoTabs/Tabs'
import { ContractsColWrapper } from '@crm/components/InfoTabs/Tabs/ContractsCol/ContractsColWrapper'
import { ProductsColWrapper } from '@crm/components/InfoTabs/Tabs/ProductsCol/ProductsColWrapper'
import { Agents } from '@crm/features/agents/pages'
import {
  CreateInvoicePage,
  InvoiceDetailsPage,
  InvoicesPage,
  ReportsPage,
  SettingsPage
} from '@crm/features/billing/pages'
import { Calls } from '@crm/features/calls/pages'
import { Campaigns, Steps } from '@crm/features/campaigns/pages'
import {
  ClientContactsCol,
  ClientContractsCol,
  ClientLeadsCol,
  ClientLogsCol,
  ClientOpportunitiesCol,
  ClientTasksCol
} from '@crm/features/clients/components/tabs'
import { Clients, ClientsDetails } from '@crm/features/clients/pages'
import { Contacts } from '@crm/features/contacts/pages'
import { Dashboard } from '@crm/features/dashboard/pages'
import { Documents } from '@crm/features/documents/pages'
import { Information } from '@crm/features/information/pages'
import { Integrations } from '@crm/features/integrations/pages'
import {
  LeadGenerationCampaign,
  LeadGenerationCampaigns
} from '@crm/features/lead-generation/pages'
import {
  CheckDuplicates,
  ConfirmField,
  ConfirmImport,
  LeadCreate,
  LeadImport,
  LeadImportSteps,
  Leads,
  LeadsDetails,
  LeadsKanban,
  PipelineStatus,
  StartImport,
  UploadFileImport
} from '@crm/features/leads/pages'
import { Logs } from '@crm/features/logs/pages'
import { NomenclaturesLayout } from '@crm/features/nomenclatures/layouts'
import {
  NomenclaturesCountries,
  NomenclaturesCurrencies,
  NomenclaturesCustomFields,
  NomenclaturesCustomTask,
  NomenclaturesDocuments,
  NomenclaturesEmailTemplates,
  NomenclaturesGeneral,
  NomenclaturesIndustries,
  NomenclaturesJobPositions,
  NomenclaturesLeads,
  NomenclaturesLeadSource,
  NomenclaturesLeadStatus,
  NomenclaturesLeadTypes,
  NomenclaturesLeadTypesStatuses,
  NomenclaturesMailbox,
  NomenclaturesMailboxAuth,
  NomenclaturesOpportunities,
  NomenclaturesOpportunityPipelines,
  NomenclaturesOpportunityPipelineStatus,
  NomenclaturesOpportunityStatus,
  NomenclaturesProducts,
  NomenclaturesProductsCatalog,
  NomenclaturesProductsCategories,
  NomenclaturesProductsUnitsOfMeasure,
  NomenclaturesReasons,
  NomenclaturesSeniorPositions,
  NomenclaturesSizes,
  NomenclaturesSubIndustries,
  NomenclaturesSystemTask,
  NomenclaturesTags,
  NomenclaturesTasks,
  NomenclaturesTasksStatuses,
  NomenclaturesTemplatesTask
} from '@crm/features/nomenclatures/pages'
import {
  Opportunities,
  OpportunitiesCreatePage,
  OpportunitiesDetails,
  OpportunitiesKanban
} from '@crm/features/opportunities/pages'

import { ReportsLayout } from '@crm/features/reports/layout'
import {
  GenericReport,
  ReportEditor,
  ReportsManagementLayout
} from '@crm/features/reports/management'
import {
  ReportDashboard,
  ReportExpenses,
  ReportProducts,
  ReportSalesFunnel,
  TrendsetterReports
} from '@crm/features/reports/pages'
import { Settings } from '@crm/features/settings/pages'
import { Tasks, TasksKanban } from '@crm/features/tasks/pages'
import { Route, UserPermissionsCRM, UserRoleCRM } from '@crm/types'
import { CONFIGURATION_ROLES, OPERATIONAL_ROLES } from '@crm/utils/permissions'

import { Status } from 'iconsax-react'

import { paths } from './paths'

export const routes: Route[] = [
  {
    name: 'Dashboard',
    path: '/',
    Component: Dashboard,
    permission: UserPermissionsCRM.DASHBOARD
  },
  {
    name: 'Leads',
    path: '/leads',
    Component: Leads,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'LeadsCreate',
    path: paths.leadsCreate,
    Component: LeadCreate,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'LeadImport',
    path: '/leads/import',
    Component: LeadImport,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'LeadsKanban',
    path: '/leads/kanban',
    Component: LeadsKanban,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'LeadsDetailsEdit',
    path: paths.leadsDetails.edit(':id'),
    Component: LeadCreate,
    permission: UserPermissionsCRM.CLIENTS
  },
  {
    name: 'LeadsDetails',
    path: '/leads/:id',
    Component: LeadsDetails,
    permission: UserPermissionsCRM.CLIENTS,
    children: [
      {
        name: 'LeadsDetailsActivity',
        index: true,
        Component: ActivityCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'LeadsDetailsOpportunity',
        path: 'opportunities',
        Component: OpportunitiesCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'LeadsDetailsContracts',
        path: 'contracts',
        Component: ContractsCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'LeadsDetailsTasks',
        path: 'tasks',
        Component: TasksCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'LeadsDetailsDocuments',
        path: 'documents',
        Component: DocumentsCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'LeadsDetailsContacts',
        path: 'contacts',
        Component: LeadsCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'LeadsDetailsCampaigns',
        path: 'campaigns',
        Component: CampaignsCol,
        permission: UserPermissionsCRM.CLIENTS
      }
    ]
  },
  {
    name: 'LeadsListView',
    path: '/leads/list-view/:id',
    Component: Leads,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'LeadImportSteps',
    path: '/leads/import/:id',
    Component: LeadImportSteps,
    permission: UserPermissionsCRM.LEADS,
    children: [
      {
        name: 'UploadFile',
        path: `first`,
        step: 'first',
        Component: UploadFileImport
      },
      {
        name: 'ConfirmImport',
        path: `${StepEnum.CONFIRM_IMPORTED_FILE}`,
        step: StepEnum.CONFIRM_IMPORTED_FILE,
        Component: ConfirmImport
      },
      {
        name: 'ConfirmField',
        path: `${StepEnum.CONFIRM_FIELD_MAPPING}`,
        step: StepEnum.CONFIRM_FIELD_MAPPING,
        Component: ConfirmField
      },
      {
        name: 'CheckDuplicates',
        path: `${StepEnum.CHECK_FOR_POSSIBLE_DUPLICATES}`,
        step: StepEnum.CHECK_FOR_POSSIBLE_DUPLICATES,
        Component: CheckDuplicates
      },
      {
        name: 'PipelineStatus',
        path: `${StepEnum.PIPELINE_STATUS_MAPPING}`,
        step: StepEnum.PIPELINE_STATUS_MAPPING,
        Component: PipelineStatus
      },
      {
        name: 'StartImport',
        path: `${StepEnum.START_IMPORT}`, //todo check use route match router sub routing
        step: StepEnum.START_IMPORT,
        Component: StartImport
      }
    ]
  },
  {
    name: 'Leads',
    path: '/leads-list/:id',
    Component: Leads,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'LeadsKanban',
    path: '/leads-list/:id/kanban',
    Component: LeadsKanban,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'Opportunities',
    path: paths.opportunities,
    Component: Opportunities,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'OpportunitiesCreate',
    path: paths.opportunitiesCreate,
    Component: OpportunitiesCreatePage,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'OpportunitiesEdit',
    path: paths.opportunitiesDetails.edit(':opportunityId', ':id'),
    Component: OpportunitiesCreatePage,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'Opportunities',
    path: '/opportunities/kanban',
    Component: OpportunitiesKanban,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'OpportunitiesDetails',
    path: paths.opportunitiesDetails.root(':opportunityId', ':id'),
    Component: OpportunitiesDetails,
    permission: UserPermissionsCRM.LEADS,
    children: [
      {
        name: 'OpportunitiesDetailsActivity',
        index: true,
        Component: ActivityCol,
        permission: UserPermissionsCRM.LEADS
      },
      {
        name: 'OpportunitiesDetailsContracts',
        path: paths.opportunitiesDetails.contracts(':opportunityId', ':id'),
        Component: ContractsColWrapper,
        permission: UserPermissionsCRM.LEADS
      },
      {
        name: 'OpportunitiesDetailsTasks',
        path: paths.opportunitiesDetails.tasks(':opportunityId', ':id'),
        Component: TasksCol,
        permission: UserPermissionsCRM.LEADS
      },
      {
        name: 'OpportunitiesDetailsDocuments',
        path: paths.opportunitiesDetails.documents(':opportunityId', ':id'),
        Component: DocumentsCol,
        permission: UserPermissionsCRM.LEADS
      },
      {
        name: 'OpportunitiesDetailsContacts',
        path: paths.opportunitiesDetails.contacts(':opportunityId', ':id'),
        Component: LeadsCol,
        permission: UserPermissionsCRM.LEADS
      },
      {
        name: 'OpportunitiesDetailsProducts',
        path: paths.opportunitiesDetails.products(':opportunityId', ':id'),
        Component: ProductsColWrapper,
        permission: UserPermissionsCRM.LEADS
      },
      {
        name: 'OpportunitiesDetailsOffers',
        path: paths.opportunitiesDetails.offers(':opportunityId', ':id'),
        Component: OffersCol,
        permission: UserPermissionsCRM.LEADS
      },
      {
        name: 'OpportunitiesDetailsPaymentAccounts',
        path: paths.opportunitiesDetails.paymentAccounts(
          ':opportunityId',
          ':id'
        ),
        Component: PaymentAccountsCol,
        permission: UserPermissionsCRM.LEADS
      }
    ]
  },
  {
    name: 'OpportunitiesKanban',
    path: '/opportunities-list/:id/',
    Component: Opportunities,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'OpportunitiesKanban',
    path: '/opportunities-list/:id/kanban',
    Component: OpportunitiesKanban,
    permission: UserPermissionsCRM.LEADS
  },
  {
    name: 'Clients',
    path: '/clients',
    Component: Clients,
    permission: UserPermissionsCRM.CLIENTS
  },
  {
    name: 'ClientsDetails',
    path: '/clients/:id',
    Component: ClientsDetails,
    permission: UserPermissionsCRM.CLIENTS,
    children: [
      {
        name: 'ClientsDetailsContacts',
        index: true,
        Component: ClientContactsCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'ClientsDetailsLeads',
        path: 'leads',
        Component: ClientLeadsCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'ClientsDetailsContracts',
        path: 'contracts',
        Component: ClientContractsCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'ClientsDetailsOpportunities',
        path: 'opportunities',
        Component: ClientOpportunitiesCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'ClientsDetailsTasks',
        path: 'tasks',
        Component: ClientTasksCol,
        permission: UserPermissionsCRM.CLIENTS
      },
      {
        name: 'ClientsDetailsLogs',
        path: 'logs',
        Component: ClientLogsCol,
        permission: UserPermissionsCRM.CLIENTS
      }
    ]
  },
  {
    name: 'Contacts',
    path: '/contacts',
    Component: Contacts,
    permission: UserPermissionsCRM.LEADS,
    roles: [UserRoleCRM.admin]
  },
  {
    name: 'Tasks',
    path: '/tasks',
    Component: Tasks,
    permission: UserPermissionsCRM.TASKS,
    // Managers and operators need this too — the dashboard task cards link
    // here. The backend narrows operators to their own or created tasks.
    roles: OPERATIONAL_ROLES
  },
  {
    name: 'TasksKanban',
    path: '/tasks/kanban',
    Component: TasksKanban,
    permission: UserPermissionsCRM.TASKS,
    roles: OPERATIONAL_ROLES
  },

  {
    name: 'Logs',
    path: paths.logs,
    Component: Logs,
    permission: UserPermissionsCRM.ADMIN,
    // Operators reach the journal too — the backend narrows it to their own
    // leads' history and their own actions.
    roles: OPERATIONAL_ROLES
  },

  {
    name: 'Calls',
    path: '/calls',
    Component: Calls,
    permission: UserPermissionsCRM.CALLS,
    roles: [UserRoleCRM.admin]
  },

  {
    name: 'Documents',
    path: '/documents',
    Component: Documents,
    permission: UserPermissionsCRM.DOCUMENTS,
    roles: [UserRoleCRM.admin]
  },

  {
    name: 'Reports',
    path: '/report',
    Component: ReportsLayout,
    permission: UserPermissionsCRM.REPORTS,
    roles: [UserRoleCRM.admin],
    children: [
      {
        name: 'ReportDashboard',
        path: `dashboard`,
        Component: ReportDashboard
      },
      {
        name: 'ReportExpenses',
        path: `expenses`,
        Component: ReportExpenses
      },
      {
        name: 'ReportSalesFunnel',
        path: `sales-funnel`,
        Component: ReportSalesFunnel
      },
      {
        name: 'ReportProducts',
        path: `products`,
        Component: ReportProducts
      },
      {
        name: 'TrendsetterReports',
        path: `trendsetter`,
        Component: TrendsetterReports
      },
      {
        name: 'ReportGeneric',
        path: `generic/:id`,
        Component: GenericReport
      }
    ]
  },

  {
    name: 'Reports',
    path: '/report-management',
    Component: ReportsManagementLayout,
    permission: UserPermissionsCRM.ADMIN,
    roles: [UserRoleCRM.admin],
    children: [
      {
        name: 'ReportEditor',
        path: `:id`,
        Component: ReportEditor
      }
    ]
  },

  {
    name: 'Nomenclatures',
    path: paths.admin.nomenclatures.root,
    Component: NomenclaturesLayout,
    permission: UserPermissionsCRM.ADMIN,
    // Global catalogs are readable by every operational role; only admins get
    // the create/edit/delete/reorder controls (see CATALOG_MANAGER_ROLES).
    roles: OPERATIONAL_ROLES,
    children: [
      {
        name: 'NomenclaturesGeneral',
        label: 'general',
        path: paths.admin.nomenclatures.general.root,
        Component: NomenclaturesGeneral,
        children: [
          {
            name: 'NomenclaturesIndustries',
            label: 'industries',
            index: true,
            Component: NomenclaturesIndustries
          },
          {
            name: 'NomenclaturesSubIndustries',
            label: 'subIndustries',
            path: paths.admin.nomenclatures.general.industry(':id'),
            Component: NomenclaturesSubIndustries
          },
          {
            name: 'NomenclaturesReasons',
            label: 'reasons',
            path: paths.admin.nomenclatures.general.reasons,
            Component: NomenclaturesReasons
          },
          {
            name: 'NomenclaturesSize',
            label: 'sizes',
            path: paths.admin.nomenclatures.general.sizes,
            Component: NomenclaturesSizes
          },
          {
            name: 'NomenclaturesCurrency',
            label: 'currencies',
            path: paths.admin.nomenclatures.general.currencies,
            Component: NomenclaturesCurrencies
          },
          {
            name: 'NomenclaturesDocuments',
            label: 'documents',
            path: paths.admin.nomenclatures.general.documents,
            Component: NomenclaturesDocuments
          },
          {
            name: 'EmailTemplates',
            label: 'email-templates',
            path: paths.admin.nomenclatures.general.emailTemplates,
            Component: NomenclaturesEmailTemplates
          },
          {
            name: 'NomenclaturesMailbox',
            path: paths.admin.nomenclatures.general.mailboxes,
            label: 'mailboxes',
            Component: NomenclaturesMailbox,
            // Mailbox connections are configuration, not a catalog — admin only,
            // even though they sit under the nomenclatures routes.
            roles: CONFIGURATION_ROLES
          },
          {
            name: 'NomenclaturesMailboxAuth',
            path: paths.admin.nomenclatures.general.mailboxesAuth,
            label: 'mailboxesAuth',
            Component: NomenclaturesMailboxAuth,
            roles: CONFIGURATION_ROLES
          },
          {
            name: 'NomenclaturesTags',
            path: paths.admin.nomenclatures.general.tags,
            label: 'tags',
            Component: NomenclaturesTags
          },
          {
            name: 'NomenclaturesCountries',
            path: paths.admin.nomenclatures.general.countries,
            label: 'countries',
            Component: NomenclaturesCountries
          },
          {
            name: 'NomenclaturesSeniorPositions',
            path: paths.admin.nomenclatures.general.seniorPositions,
            label: 'seniorPositions',
            Component: NomenclaturesSeniorPositions
          },
          {
            name: 'NomenclaturesJobPositions',
            path: paths.admin.nomenclatures.general.seniorPosition(':id'),
            label: 'jobPositions',
            Component: NomenclaturesJobPositions
          },
          {
            name: 'NomenclaturesCustomFields',
            path: paths.admin.nomenclatures.general.customFields,
            label: 'customFields',
            Component: NomenclaturesCustomFields
          }
        ]
      },

      {
        name: 'NomenclaturesLeads',
        label: 'leads',
        path: paths.admin.nomenclatures.leads.root,
        Component: NomenclaturesLeads,
        children: [
          {
            name: 'NomenclaturesLeadTypes',
            index: true,
            label: 'leadTypes',
            Component: NomenclaturesLeadTypes
          },
          {
            name: 'NomenclaturesLeadStatus',
            label: 'leadStatus',
            path: paths.admin.nomenclatures.leads.statuses,
            Component: NomenclaturesLeadStatus
          },
          {
            name: 'NomenclaturesLeadSource',
            label: 'leadSource',
            path: paths.admin.nomenclatures.leads.sources,
            Component: NomenclaturesLeadSource
          },
          {
            name: 'NomenclaturesLeadTypesStatuses',
            path: paths.admin.nomenclatures.leads.type(':id'),
            label: 'leadTypesStatus',
            Component: NomenclaturesLeadTypesStatuses
          }
        ]
      },

      {
        name: 'NomenclaturesOpportunities',
        label: 'opportunities',
        path: paths.admin.nomenclatures.opportunities.root,
        Component: NomenclaturesOpportunities,
        children: [
          {
            name: 'NomenclaturesOpportunityPipelines',
            label: 'opportunityPipelines',
            index: true,
            Component: NomenclaturesOpportunityPipelines
          },
          {
            name: 'NomenclaturesOpportunityPipelineStatuses',
            label: 'opportunityPipelineStatuses',
            path: paths.admin.nomenclatures.opportunities.pipeline(':id'),
            Component: NomenclaturesOpportunityPipelineStatus
          },
          {
            name: 'NomenclaturesOpportunityStatus',
            label: 'opportunityStatus',
            path: paths.admin.nomenclatures.opportunities.statuses,
            Component: NomenclaturesOpportunityStatus
          }
        ]
      },

      {
        name: 'NomenclaturesTasks',
        label: 'tasks',
        path: paths.admin.nomenclatures.tasks.root,
        Component: NomenclaturesTasks,
        children: [
          {
            name: 'NomenclaturesSystemTask',
            label: 'tasks_types',
            index: true,
            Component: NomenclaturesSystemTask
          },
          {
            name: 'NomenclaturesCustomTask',
            path: paths.admin.nomenclatures.tasks.custom,
            iconComponent: UserOutlined,
            Component: NomenclaturesCustomTask
          },
          {
            name: 'NomenclaturesTemplatesTask',
            path: paths.admin.nomenclatures.tasks.templates,
            iconComponent: BlockOutlined,
            Component: NomenclaturesTemplatesTask
          },
          {
            name: 'NomenclaturesStatusesTask',
            path: paths.admin.nomenclatures.tasks.statuses,
            iconComponent: Status,
            Component: NomenclaturesTasksStatuses
          }
        ]
      },

      {
        name: 'NomenclaturesProducts',
        label: 'products',
        path: paths.admin.nomenclatures.products.root,
        Component: NomenclaturesProducts,
        children: [
          {
            name: 'NomenclaturesProductsCatalog',
            label: 'productsCatalog',
            index: true,
            Component: NomenclaturesProductsCatalog
          },
          {
            name: 'NomenclaturesProductsCategories',
            path: paths.admin.nomenclatures.products.categories,
            label: 'productsCategories',
            Component: NomenclaturesProductsCategories
          },
          {
            name: 'NomenclaturesProductsUnitMeasure',
            path: paths.admin.nomenclatures.products.unitMeasure,
            label: 'productsUnitMeasure',
            Component: NomenclaturesProductsUnitsOfMeasure
          }
        ]
      }
    ]
  },

  {
    name: 'Agents',
    path: '/agents',
    Component: Agents,
    permission: UserPermissionsCRM.ADMIN,
    roles: [UserRoleCRM.admin]
  },

  {
    name: 'Integrations',
    path: paths.admin.integrations.root,
    Component: Integrations,
    permission: UserPermissionsCRM.ADMIN,
    roles: [UserRoleCRM.admin]
  },

  {
    name: 'Settings',
    path: '/settings',
    Component: Settings,
    permission: UserPermissionsCRM.ADMIN,
    roles: [UserRoleCRM.admin]
  },

  {
    name: 'BillingInvoices',
    path: '/billing/invoices',
    Component: InvoicesPage,
    permission: UserPermissionsCRM.BILLING,
    roles: [UserRoleCRM.admin]
  },
  {
    name: 'BillingInvoicesCreate',
    path: '/billing/invoices/create',
    Component: CreateInvoicePage,
    permission: UserPermissionsCRM.BILLING,
    roles: [UserRoleCRM.admin]
  },
  {
    name: 'BillingInvoicesDetails',
    path: '/billing/invoices/:id',
    Component: InvoiceDetailsPage,
    permission: UserPermissionsCRM.BILLING,
    roles: [UserRoleCRM.admin]
  },
  {
    name: 'BillingReports',
    path: '/billing/reports',
    Component: ReportsPage,
    permission: UserPermissionsCRM.BILLING,
    roles: [UserRoleCRM.admin]
  },
  {
    name: 'BillingSettings',
    path: '/billing/company-settings',
    Component: SettingsPage,
    permission: UserPermissionsCRM.BILLING,
    roles: [UserRoleCRM.admin]
  },

  {
    name: 'Campaigns',
    path: '/campaigns',
    Component: Campaigns,
    permission: UserPermissionsCRM.CAMPAIGNS,
    roles: [UserRoleCRM.admin]
  },
  {
    name: 'Steps',
    path: '/campaigns/:id',
    Component: Steps,
    permission: UserPermissionsCRM.CAMPAIGNS,
    roles: [UserRoleCRM.admin]
  },
  {
    name: 'LeadGeneration',
    path: paths.leadGeneration.root,
    Component: LeadGenerationCampaigns,
    permission: UserPermissionsCRM.CAMPAIGNS,
    roles: [UserRoleCRM.admin]
  },
  {
    name: 'LeadGenerationCampaign',
    path: paths.leadGeneration.campaign(':generatorKey'),
    Component: LeadGenerationCampaign,
    permission: UserPermissionsCRM.CAMPAIGNS,
    roles: [UserRoleCRM.admin]
  },
  {
    name: 'Information',
    path: '/information',
    Component: Information,
    permission: UserPermissionsCRM.DASHBOARD,
    roles: [UserRoleCRM.admin]
  }
]
